import React, { useState } from 'react';
import {
    Box, Card, CardContent, Typography, Grid, TextField, FormControl,
    InputLabel, Select, MenuItem, Chip, Button, Paper,
    FormControlLabel, Checkbox, Alert, Stepper, Step, StepLabel,
    LinearProgress, Accordion, AccordionSummary, AccordionDetails, useTheme, alpha
} from '@mui/material';
import {
    Add as AddIcon,
    DeleteOutline as TrashIcon,
    CheckCircle as CheckIcon,
    EmojiEvents as TrophyIcon,
    School as LearningIcon,
    Share as ShareIcon,
    Assessment as MetricsIcon,
    Celebration as CelebrationIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

export default function D8Recognition({ step, onStepUpdate, stepMapping, stepId, isReadOnly, batchTimeFormat = '12hour' }) {
    const theme = useTheme();
    const [activePhase, setActivePhase] = useState(0);

    const updateStep = (updates) => {
        onStepUpdate(stepMapping[stepId], { ...step, ...updates });
    };

    const recognitionPhases = [
        { name: 'Team Recognition', icon: <TrophyIcon />, color: '#ff9800' },
        { name: 'Lessons Learned', icon: <LearningIcon />, color: '#2196f3' },
        { name: 'Knowledge Sharing', icon: <ShareIcon />, color: '#4caf50' },
        { name: 'Success Metrics', icon: <MetricsIcon />, color: '#9c27b0' },
        { name: 'Celebration', icon: <CelebrationIcon />, color: '#f44336' }
    ];

    const addTeamMember = () => {
        const newMembers = [...(step.teamMembers || []), {
            id: Date.now(),
            name: '',
            role: '',
            department: '',
            contribution: '',
            recognition: '',
            impact: ''
        }];
        updateStep({ teamMembers: newMembers });
    };

    const updateTeamMember = (index, field, value) => {
        const updatedMembers = [...(step.teamMembers || [])];
        updatedMembers[index] = { ...updatedMembers[index], [field]: value };
        updateStep({ teamMembers: updatedMembers });
    };

    const deleteTeamMember = (index) => {
        const updatedMembers = (step.teamMembers || []).filter((_, i) => i !== index);
        updateStep({ teamMembers: updatedMembers });
    };

    const addLesson = () => {
        const newLessons = [...(step.lessonsLearned || []), {
            id: Date.now(),
            category: '',
            lesson: '',
            application: '',
            stakeholders: '',
            priority: 'medium'
        }];
        updateStep({ lessonsLearned: newLessons });
    };

    const updateLesson = (index, field, value) => {
        const updatedLessons = [...(step.lessonsLearned || [])];
        updatedLessons[index] = { ...updatedLessons[index], [field]: value };
        updateStep({ lessonsLearned: updatedLessons });
    };

    const deleteLesson = (index) => {
        const updatedLessons = (step.lessonsLearned || []).filter((_, i) => i !== index);
        updateStep({ lessonsLearned: updatedLessons });
    };

    const getPhaseCompletionStatus = (phaseIndex) => {
        switch (phaseIndex) {
            case 0: // Team Recognition
                return step.teamSummary?.trim() && 
                       step.keyContributors?.trim() && 
                       (step.teamMembers || []).length > 0;
            case 1: // Lessons Learned
                return step.criticalLessons?.trim() && 
                       step.processImprovements?.trim() && 
                       (step.lessonsLearned || []).length > 0;
            case 2: // Knowledge Sharing
                return step.sharingPlan?.trim() && 
                       step.documentationPlan?.trim() && 
                       step.trainingPlan?.trim();
            case 3: // Success Metrics
                return step.successMetrics?.trim() && 
                       step.impactAssessment?.trim() && 
                       step.futurePreventionValue?.trim();
            case 4: // Celebration
                return step.celebrationPlan?.trim() && 
                       step.communicationPlan?.trim() && 
                       step.finalSummary?.trim();
            default:
                return false;
        }
    };

    // Overall form validation function
    const isFormValid = () => {
        return getPhaseCompletionStatus(0) && 
               getPhaseCompletionStatus(1) && 
               getPhaseCompletionStatus(2) && 
               getPhaseCompletionStatus(3) && 
               getPhaseCompletionStatus(4);
    };

    const renderTeamRecognition = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#ff9800', 0.1)}, ${alpha('#ff9800', 0.05)})`,
                        border: `1px solid ${alpha('#ff9800', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <TrophyIcon sx={{ fontSize: 24, color: '#ff9800' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Team Recognition & Contributions
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Recognize team achievements and contributions
                        </Typography>
                    </Box>
                    {getPhaseCompletionStatus(0) && (
                        <Chip label="COMPLETED" color="success" sx={{ fontWeight: 500 }} icon={<CheckIcon />} />
                    )}
                </Box>
            
            <Alert severity={getPhaseCompletionStatus(0) ? "success" : "info"} sx={{ mb: 3 }}>
                Recognize team members and their valuable contributions to solving this deviation.
            </Alert>

                <Grid container spacing={4} sx={{ flexGrow: 1 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Team Success Summary"
                            value={step.teamSummary || ''}
                            onChange={isReadOnly ? undefined : (e) => updateStep({ teamSummary: e.target.value })}
                            InputProps={{ readOnly: isReadOnly }}
                            placeholder="Overall summary of team success and achievements..."
                            sx={{ 
                                mb: 3,
                                '& .MuiOutlinedInput-root': { 
                                    borderRadius: 2,
                                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                }
                            }}
                        />
                    </Grid>
                
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Key Contributors"
                            value={step.keyContributors || ''}
                            onChange={isReadOnly ? undefined : (e) => updateStep({ keyContributors: e.target.value })}
                            InputProps={{ readOnly: isReadOnly }}
                            placeholder="Highlight key contributors and their specific contributions..."
                            sx={{ 
                                mb: 3,
                                '& .MuiOutlinedInput-root': { 
                                    borderRadius: 2,
                                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Team Strengths Demonstrated"
                            value={step.teamStrengths || ''}
                            onChange={isReadOnly ? undefined : (e) => updateStep({ teamStrengths: e.target.value })}
                            InputProps={{ readOnly: isReadOnly }}
                            placeholder="What strengths did the team demonstrate during this investigation..."
                            sx={{ 
                                '& .MuiOutlinedInput-root': { 
                                    borderRadius: 2,
                                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                }
                            }}
                        />
                    </Grid>
                
                    <Grid item xs={12}>
                        <Accordion sx={{
                            border: `1px solid ${theme.palette.grey[400]}`,
                            borderRadius: 2,
                            boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
                            '&:before': { display: 'none' },
                            '&.Mui-expanded': { margin: 0 }
                        }}>
                            <AccordionSummary 
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    borderRadius: 2,
                                    '&.Mui-expanded': { minHeight: 48 },
                                    '& .MuiAccordionSummary-content': { margin: '12px 0' }
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                    Individual Recognition ({(step.teamMembers || []).length} members)
                                </Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            {(step.teamMembers || []).map((member, index) => (
                                <Paper key={member.id} sx={{ 
                                    p: 3, 
                                    mb: 3, 
                                    border: `1px solid ${theme.palette.grey[400]}`, 
                                    borderRadius: 2, 
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`
                                }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="Team Member Name"
                                                value={member.name}
                                                onChange={isReadOnly ? undefined : (e) => updateTeamMember(index, 'name', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="Full name"
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="Role/Position"
                                                value={member.role}
                                                onChange={isReadOnly ? undefined : (e) => updateTeamMember(index, 'role', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="Job title/role"
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="Department"
                                                value={member.department}
                                                onChange={isReadOnly ? undefined : (e) => updateTeamMember(index, 'department', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="Department name"
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Specific Contribution"
                                                value={member.contribution}
                                                onChange={isReadOnly ? undefined : (e) => updateTeamMember(index, 'contribution', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="What did this person specifically contribute to the investigation..."
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={<TrashIcon />}
                                                    onClick={isReadOnly ? undefined : () => deleteTeamMember(index)}
                                                    disabled={isReadOnly}
                                                    sx={{ 
                                                        borderRadius: 2, 
                                                        px: 3,
                                                        opacity: isReadOnly ? 0.6 : 1
                                                    }}
                                                >
                                                    Remove Member
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            ))}
                            
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={isReadOnly ? undefined : addTeamMember}
                                disabled={isReadOnly}
                                sx={{ 
                                    mt: 2,
                                    borderRadius: 2,
                                    fontWeight: 500,
                                    px: 3,
                                    py: 1,
                                    opacity: isReadOnly ? 0.6 : 1,
                                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                                }}
                            >
                                Add Team Member
                            </Button>
                        </AccordionDetails>
                    </Accordion>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    const renderLessonsLearned = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#2196f3', 0.1)}, ${alpha('#2196f3', 0.05)})`,
                        border: `1px solid ${alpha('#2196f3', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <LearningIcon sx={{ fontSize: 24, color: '#2196f3' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Lessons Learned & Process Improvements
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Document critical lessons and improvements
                        </Typography>
                    </Box>
                    {getPhaseCompletionStatus(1) && (
                        <Chip label="COMPLETED" color="success" sx={{ fontWeight: 500 }} icon={<CheckIcon />} />
                    )}
                </Box>
            
            <Alert severity={getPhaseCompletionStatus(1) ? "success" : "info"} sx={{ mb: 3 }}>
                Document critical lessons learned and process improvements for future application.
            </Alert>

                <Grid container spacing={4} sx={{ flexGrow: 1 }}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            multiline
                            rows={5}
                            label="Critical Lessons Learned"
                        value={step.criticalLessons || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ criticalLessons: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Most important lessons learned from this investigation..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Process Improvements Identified"
                        value={step.processImprovements || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ processImprovements: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Process improvements identified for implementation..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <Accordion sx={{
                        border: `1px solid ${theme.palette.grey[400]}`,
                        borderRadius: 2,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': { margin: 0 }
                    }}>
                        <AccordionSummary 
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                borderRadius: 2,
                                '&.Mui-expanded': { minHeight: 48 },
                                '& .MuiAccordionSummary-content': { margin: '12px 0' }
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                Detailed Lessons ({(step.lessonsLearned || []).length} lessons)
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {(step.lessonsLearned || []).map((lesson, index) => (
                                <Paper key={lesson.id} sx={{ 
                                    p: 3, 
                                    mb: 3, 
                                    border: `1px solid ${theme.palette.grey[400]}`, 
                                    borderRadius: 2, 
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`
                                }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth sx={{ 
                                                mb: 2,
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[400],
                                                    borderWidth: 1
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[600],
                                                    borderWidth: 1
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[700],
                                                    borderWidth: 1
                                                }
                                            }}>
                                                <InputLabel>Category</InputLabel>
                                                <Select
                                                    value={lesson.category}
                                                    onChange={isReadOnly ? undefined : (e) => updateLesson(index, 'category', e.target.value)}
                                                    inputProps={{ readOnly: isReadOnly }}
                                                    label="Category"
                                                    sx={{ borderRadius: 2 }}
                                                >
                                                    <MenuItem value="investigation">Investigation Process</MenuItem>
                                                    <MenuItem value="root_cause">Root Cause Analysis</MenuItem>
                                                    <MenuItem value="prevention">Prevention Strategy</MenuItem>
                                                    <MenuItem value="communication">Communication</MenuItem>
                                                    <MenuItem value="teamwork">Teamwork</MenuItem>
                                                    <MenuItem value="technical">Technical</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth sx={{ 
                                                mb: 2,
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[400],
                                                    borderWidth: 1
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[600],
                                                    borderWidth: 1
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.grey[700],
                                                    borderWidth: 1
                                                }
                                            }}>
                                                <InputLabel>Priority</InputLabel>
                                                <Select
                                                    value={lesson.priority}
                                                    onChange={isReadOnly ? undefined : (e) => updateLesson(index, 'priority', e.target.value)}
                                                    inputProps={{ readOnly: isReadOnly }}
                                                    label="Priority"
                                                    sx={{ borderRadius: 2 }}
                                                >
                                                    <MenuItem value="critical">Critical</MenuItem>
                                                    <MenuItem value="high">High</MenuItem>
                                                    <MenuItem value="medium">Medium</MenuItem>
                                                    <MenuItem value="low">Low</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Lesson Learned"
                                                value={lesson.lesson}
                                                onChange={isReadOnly ? undefined : (e) => updateLesson(index, 'lesson', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="Describe the specific lesson learned..."
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={<TrashIcon />}
                                                    onClick={isReadOnly ? undefined : () => deleteLesson(index)}
                                                    disabled={isReadOnly}
                                                    sx={{ 
                                                        borderRadius: 2, 
                                                        px: 3,
                                                        opacity: isReadOnly ? 0.6 : 1
                                                    }}
                                                >
                                                    Remove Lesson
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            ))}
                            
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={isReadOnly ? undefined : addLesson}
                                disabled={isReadOnly}
                                sx={{ 
                                    mt: 2,
                                    borderRadius: 2,
                                    fontWeight: 500,
                                    px: 3,
                                    py: 1,
                                    opacity: isReadOnly ? 0.6 : 1,
                                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                                }}
                            >
                                Add Lesson Learned
                            </Button>
                        </AccordionDetails>
                    </Accordion>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    const renderKnowledgeSharing = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#4caf50', 0.1)}, ${alpha('#4caf50', 0.05)})`,
                        border: `1px solid ${alpha('#4caf50', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <ShareIcon sx={{ fontSize: 24, color: '#4caf50' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Knowledge Sharing & Documentation
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Share knowledge and document findings
                        </Typography>
                    </Box>
                    {getPhaseCompletionStatus(2) && (
                        <Chip label="COMPLETED" color="success" sx={{ fontWeight: 500 }} icon={<CheckIcon />} />
                    )}
                </Box>
            
            <Alert severity={getPhaseCompletionStatus(2) ? "success" : "info"} sx={{ mb: 3 }}>
                Plan how to share knowledge and document learnings for organizational benefit.
            </Alert>

                <Grid container spacing={4} sx={{ flexGrow: 1 }}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Knowledge Sharing Plan"
                        value={step.sharingPlan || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ sharingPlan: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="How will lessons be shared across the organization..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Documentation & Archive Plan"
                        value={step.documentationPlan || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ documentationPlan: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="How will this case be documented for future reference..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Training & Education Plan"
                        value={step.trainingPlan || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ trainingPlan: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="How will learnings be incorporated into training programs..."
                        sx={{ 
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    const renderSuccessMetrics = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#9c27b0', 0.1)}, ${alpha('#9c27b0', 0.05)})`,
                        border: `1px solid ${alpha('#9c27b0', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <MetricsIcon sx={{ fontSize: 24, color: '#9c27b0' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Success Metrics & Impact Assessment
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Quantify success and measure impact
                        </Typography>
                    </Box>
                    {getPhaseCompletionStatus(3) && (
                        <Chip label="COMPLETED" color="success" sx={{ fontWeight: 500 }} icon={<CheckIcon />} />
                    )}
                </Box>
            
            <Alert severity={getPhaseCompletionStatus(3) ? "success" : "info"} sx={{ mb: 3 }}>
                Quantify the success and impact of this investigation effort.
            </Alert>

                <Grid container spacing={4} sx={{ flexGrow: 1 }}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Success Metrics & Achievements"
                        value={step.successMetrics || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ successMetrics: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Measurable successes and achievements from this investigation..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Impact Assessment"
                        value={step.impactAssessment || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ impactAssessment: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Overall impact on quality, safety, efficiency, cost..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Future Prevention Value"
                        value={step.futurePreventionValue || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ futurePreventionValue: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Expected value and benefits for preventing future similar deviations..."
                        sx={{ 
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    const renderCelebration = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#f44336', 0.1)}, ${alpha('#f44336', 0.05)})`,
                        border: `1px solid ${alpha('#f44336', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <CelebrationIcon sx={{ fontSize: 24, color: '#f44336' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Celebration & Communication
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Celebrate success and communicate achievements
                        </Typography>
                    </Box>
                    {getPhaseCompletionStatus(4) && (
                        <Chip label="COMPLETED" color="success" sx={{ fontWeight: 500 }} icon={<CheckIcon />} />
                    )}
                </Box>
            
            <Alert severity={getPhaseCompletionStatus(4) ? "success" : "info"} sx={{ mb: 3 }}>
                Plan celebration activities and communicate success to stakeholders.
            </Alert>

                <Grid container spacing={4} sx={{ flexGrow: 1 }}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Celebration Plan"
                        value={step.celebrationPlan || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ celebrationPlan: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="How will team success be celebrated and recognized..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Communication Plan"
                        value={step.communicationPlan || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ communicationPlan: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="How will success be communicated to stakeholders..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        multiline
                        rows={6}
                        label="Final Investigation Summary"
                        value={step.finalSummary || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ finalSummary: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Comprehensive final summary of the entire 8D investigation process and outcomes..."
                        sx={{ 
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    const renderPhase = () => {
        switch (activePhase) {
            case 0: return renderTeamRecognition();
            case 1: return renderLessonsLearned();
            case 2: return renderKnowledgeSharing();
            case 3: return renderSuccessMetrics();
            case 4: return renderCelebration();
            default: return null;
        }
    };

    return (
        <Box sx={{ 
            p: 4, 
            backgroundColor: alpha(theme.palette.warning.main, 0.1), 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.warning.main}`, 
            boxShadow: `0 8px 32px ${alpha(theme.palette.warning.main, 0.12)}`,
            backdropFilter: 'blur(20px)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                    p: 1.5, 
                    borderRadius: 2, 
                    background: `linear-gradient(${alpha(theme.palette.warning.main, 0.1)}, ${alpha(theme.palette.warning.main, 0.05)})`,
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                    backdropFilter: 'blur(10px)',
                    mr: 2
                }}>
                    <TrophyIcon sx={{ fontSize: 24, color: theme.palette.warning.main }} />
                </Box>
                <Box>
                    <Typography variant="h4" sx={{ 
                        fontWeight: 500, 
                        color: theme.palette.warning.dark, 
                        mb: 0.5
                    }}>
                        D8: Team Recognition & Congratulation
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Celebrate team success, document lessons learned, and ensure knowledge is shared for continuous improvement.
                    </Typography>
                </Box>
            </Box>
            
            <Stepper 
                activeStep={-1}
                sx={{ 
                    mb: 4,
                    '& .MuiStep-root': {
                        opacity: 1,
                        transition: 'opacity 0.3s ease'
                    },
                    '& .MuiStepLabel-root': {
                        cursor: 'pointer',
                        opacity: 0.4,
                        '&[data-active="true"]': {
                            opacity: 1
                        },
                        transition: 'opacity 0.3s ease'
                    },
                    '& .MuiStepIcon-root': {
                        fontSize: '1.5rem',
                        color: theme.palette.grey[400],
                        '&.Mui-active': {
                            color: theme.palette.primary.main,
                        },
                        '&.Mui-completed': {
                            color: theme.palette.grey[400],
                        }
                    },
                    '& .MuiStepIcon-text': {
                        fill: 'white',
                        fontWeight: 500
                    }
                }} 
                alternativeLabel
            >
                {recognitionPhases.map((phase, index) => (
                    <Step key={phase.name}>
                        <StepLabel 
                            onClick={() => setActivePhase(index)}
                            data-active={activePhase === index}
                            StepIconProps={{
                                icon: index + 1
                            }}
                            sx={{
                                '& .MuiStepIcon-root': {
                                    color: activePhase === index ? theme.palette.primary.main : theme.palette.grey[400],
                                    '&.Mui-completed': {
                                        color: theme.palette.grey[400],
                                    }
                                }
                            }}
                        >
                            {phase.name}
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>

            {renderPhase()}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button 
                    disabled={activePhase === 0}
                    onClick={() => setActivePhase(prev => prev - 1)}
                    variant="outlined"
                    sx={{ 
                        borderRadius: 2, 
                        px: 3,
                        fontWeight: 500,
                        textTransform: 'none',
                        borderColor: theme.palette.grey[400],
                        color: theme.palette.text.primary,
                        '&:hover': {
                            borderColor: theme.palette.grey[600],
                            backgroundColor: alpha(theme.palette.grey[600], 0.04)
                        },
                        '&:disabled': {
                            borderColor: theme.palette.grey[300],
                            color: theme.palette.grey[400]
                        }
                    }}
                >
                    Previous Phase
                </Button>
                <Button 
                    disabled={activePhase === recognitionPhases.length - 1}
                    onClick={() => setActivePhase(prev => prev + 1)}
                    variant="contained"
                    sx={{ 
                        borderRadius: 2, 
                        px: 3,
                        fontWeight: 500,
                        textTransform: 'none',
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        '&:hover': {
                            backgroundColor: theme.palette.primary.dark
                        },
                        '&:disabled': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.3),
                            color: alpha(theme.palette.primary.contrastText, 0.5)
                        }
                    }}
                >
                    Next Phase
                </Button>
            </Box>
            
            <Paper sx={{ 
                p: 3, 
                mt: 4, 
                backgroundColor: '#fff', 
                border: `1px solid ${theme.palette.grey[400]}`,
                borderRadius: 2,
                boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`
            }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={step.completed || false}
                            onChange={isReadOnly ? undefined : (e) => {
                                if (!isFormValid() && e.target.checked) {
                                    alert('Please complete all recognition phases before marking as complete.');
                                    return;
                                }
                                updateStep({ completed: e.target.checked });
                            }}
                            disabled={isReadOnly || !isFormValid()}
                            sx={{ '& .MuiSvgIcon-root': { fontSize: 28, color: isFormValid() ? '#2196f3' : '#ccc' } }}
                        />
                    }
                    label=""
                />
                <Typography variant="h6" sx={{ fontWeight: 500, color: isFormValid() ? '#0277bd' : '#666', display: 'inline' }}>
                    D8 Team Recognition Complete - Investigation Successfully Concluded!
                </Typography>
                {!isFormValid() && (
                    <Typography variant="body2" sx={{ color: '#f44336', fontStyle: 'italic', display: 'block', mt: 1 }}>
                        (Complete all phases: Team Recognition, Lessons Learned, Knowledge Sharing, Success Metrics, and Celebration)
                    </Typography>
                )}
            </Paper>
        </Box>
    );
} 